/**
 * The editor's time field: what a person types into a cue's start or end box → `Ticks`.
 *
 * This is an ABSOLUTE time, which is why it is not `parseTimeInput` from the shift tool. That
 * parser reads a bare number as milliseconds, because a shift of `1500` is the competitor's
 * habit; a cue that starts at `1500` milliseconds is nobody's habit, and a bare `83.5` typed into
 * a start box means eighty-three and a half seconds. So:
 *
 *   `00:00:01,000`   SRT
 *   `00:00:01.000`   VTT, and SRT with the wrong separator
 *   `0:00:01.00`     ASS (centiseconds; a fraction is read as a decimal, so `.5` is 500 ms)
 *   `01:02.5`        minutes:seconds, the short VTT form
 *   `83.5`  `83`     seconds
 *
 * The FIRST component is unbounded (`75:00` is seventy-five minutes) and every later one must be
 * 0–59, so `00:70:00` is flagged rather than read as 1:10:00. Milliseconds is the finest unit:
 * a fourth fraction digit is refused, never rounded away behind the user's back.
 *
 * ASCII only, like the shift parser and for its reason: '٣' is refused, not read as 3, and no
 * locale-aware number parsing runs in a keystroke handler. Failure is a CODE; the page owns the
 * words.
 */

import type { Ticks } from '../../contracts/cue-model.ts';
import { T, TICKS_PER_MS } from '../core/time/ticks.ts';
import { formatTimestamp, timeFormat } from '../core/time/timestamp.ts';

export type CueTimeError =
  /** Nothing but whitespace. */
  | 'empty'
  /** A character that is not a digit, ':' or a decimal separator, or a separator out of place. */
  | 'not-a-time'
  /** More than hours:minutes:seconds. */
  | 'too-many-parts'
  /** A minutes or seconds component after the first that is 60 or more. */
  | 'component-out-of-range'
  /** More than three digits after the decimal separator. */
  | 'fraction-too-long'
  /** 1,000 hours or more. */
  | 'out-of-range';

export type CueTimeResult =
  | { readonly ok: true; readonly ticks: Ticks; readonly milliseconds: number }
  | { readonly ok: false; readonly code: CueTimeError };

const DIGITS = /^[0-9]+$/;
/** No subtitle file is a thousand hours long; anything past it is a typo. */
const MAX_HOURS = 1000;

export function parseCueTime(raw: string): CueTimeResult {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, code: 'empty' };

  let body = trimmed;
  let fractionMs = 0;
  const dot = body.search(/[.,]/);
  if (dot >= 0) {
    const frac = body.slice(dot + 1);
    body = body.slice(0, dot);
    if (frac === '' || !DIGITS.test(frac)) return { ok: false, code: 'not-a-time' };
    if (frac.length > 3) return { ok: false, code: 'fraction-too-long' };
    fractionMs = Number(frac.padEnd(3, '0'));
    if (body === '') body = '0';
  }

  const parts = body.split(':');
  if (parts.length > 3) return { ok: false, code: 'too-many-parts' };
  for (const part of parts) {
    if (!DIGITS.test(part)) return { ok: false, code: 'not-a-time' };
  }
  const numbers = parts.map(Number);
  // Every component after the first is bounded: minutes and seconds are 0–59.
  for (let i = 1; i < numbers.length; i++) {
    if ((numbers[i] as number) >= 60) return { ok: false, code: 'component-out-of-range' };
  }
  let seconds = 0;
  for (const n of numbers) seconds = seconds * 60 + n;
  if (seconds >= MAX_HOURS * 3600) return { ok: false, code: 'out-of-range' };

  const milliseconds = seconds * 1000 + fractionMs;
  return { ok: true, ticks: T.ticks(milliseconds * TICKS_PER_MS), milliseconds };
}

/** The display form, `hh:mm:ss.mmm`: the cue table's own dialect, so the columns line up. */
const DISPLAY = timeFormat('vtt');

export function formatCueTime(ticks: number): string {
  return formatTimestamp(T.ticks(ticks), DISPLAY);
}
